import App from '../App';
import { DeviceOrientation } from './DeviceOrientation';
import { Gamepad } from './Gamepad';
import { KeyboardControl } from './Keyboard';
import { Person } from './Person';
import { VirtualJoystick, StickVisibility } from './VirtualJoystick';

import { android, ios } from '@egr/xbox/utils/UserAgent';

import { Default } from '@egr/wcf/modules/pl/tool/Default';
import { InputState } from '@egr/wcf/modules/core/view';
import { InteractionMode } from '@egr/wcf/modules/core/tool';
import { ListenableEvent } from '@egr/wcf/modules/utils';

interface BeforeArStateBackup {
    deviceOrientationEnabled: boolean;
    fullDeviceOrientationMode: boolean;
    joystickVisibility: StickVisibility;
    personHeight: 0.8 | 1.65 | 3.5;
}

export class WalkthroughTool extends Default {
    public readonly eventHeightChanged: ListenableEvent<void, WalkthroughTool>;
    protected readonly app: App;
    protected gamepad: Gamepad;
    protected keyboard: KeyboardControl;
    protected virtualJoystick: VirtualJoystick;
    protected deviceOrientation: DeviceOrientation;
    protected arAllowed: boolean;
    protected arEnabled: boolean;
    protected stateBackup: BeforeArStateBackup;

    public set DeviceOrientationEnabled(enabled: boolean) {
        if (this.deviceOrientation) {
            this.deviceOrientation.Enabled = enabled;
        }
    }
    public set DeviceOrientationFullMode(enabled: boolean) {
        if (this.deviceOrientation) {
            this.deviceOrientation.FullOrientation = enabled;
        }
    }
    public set JoystickVisibility(visibility: StickVisibility) {
        if (this.virtualJoystick) {
            this.virtualJoystick.StickVisibility = visibility;
        }
    }
    public set ArEnabled(enabled: boolean) {
        if (this.arEnabled !== enabled) {
            this.arEnabled = enabled;
            this.toggleArMode();
        }
    }

    public constructor(app: App) {
        super(app.coreApp);
        this.app = app;

        this.app.coreApp.document.eventAfterLoad.addListener((failed: boolean): void => {
            if (!failed) {
                Person.setHeight(this.app.coreApp.viewer.view, 1.65);
                Person.lookAhead(this.app.coreApp.viewer.view);
            }
        });

        this.eventHeightChanged = new ListenableEvent<void, WalkthroughTool>(this);

        this.arEnabled = false;
        if (ios || android) {
            this.virtualJoystick = new VirtualJoystick(this.app, StickVisibility.FULL);
        } else {
            this.gamepad = new Gamepad(this.app);
            this.gamepad.eventHeightChanged.addListener(() => {
                this.eventHeightChanged.trigger();
                this.app.viewManager.setActivePersonHeightButton();
            });
            this.keyboard = new KeyboardControl(this.app);
            this.keyboard.eventHeightChanged.addListener(() => {
                this.eventHeightChanged.trigger();
                this.app.viewManager.setActivePersonHeightButton();
            });
        }
    }

    public override start(pParameter: unknown): boolean {
        this.setInteractionMode(InteractionMode.All);

        return (super.start(pParameter));
    }

    public override stop(): void {
        // nothing to do
    }

    public override handlePointerDown(pEvent: PointerEvent, pState: InputState): void {
        if (this.arEnabled) {
            super.handlePointerDown(pEvent, pState);
        }
        return;
    }

    public override handlePointerMove(pEvent: PointerEvent, pState: InputState): void {
        if (this.arEnabled) {
            super.handlePointerMove(pEvent, pState);
        }
        return;
    }

    public override handlePointerUp(pEvent: PointerEvent, pState: InputState): void {
        if (this.arEnabled) {
            super.handlePointerUp(pEvent, pState);
        }
        return;
    }

    private toggleArMode(): void {
        this.modifyToolForAR();
        if (this.arEnabled) {
            this.stateBackup = {
                deviceOrientationEnabled: this.deviceOrientation.Enabled,
                fullDeviceOrientationMode: this.deviceOrientation.FullOrientation,
                joystickVisibility: this.virtualJoystick.StickVisibility,
                personHeight: this.app.coreApp.viewer.view?.cameraControl.position.y
            } as BeforeArStateBackup;
            this.DeviceOrientationEnabled = false;
            this.DeviceOrientationFullMode = false;
            this.JoystickVisibility = StickVisibility.DISABLED;
            return;
        }
        this.DeviceOrientationEnabled = this.stateBackup.deviceOrientationEnabled;
        this.DeviceOrientationFullMode = this.stateBackup.fullDeviceOrientationMode;
        this.JoystickVisibility = this.stateBackup.joystickVisibility;
        Person.setHeight(this.app.coreApp.viewer.view, this.stateBackup.personHeight);
    }

    private modifyToolForAR(): void {
        if (this.arEnabled) {
            this.rotateXEnabled = true;
            this.rotateYEnabled = true;
            this.rotateZEnabled = true;
            this.moveXZEnabled = true;
            this.moveYEnabled = true;
            this.showElementInteractors = true;
            this.showMainElementSelection = true;
            this.app.coreApp.tools.stopActiveTool();
            return;
        }
        this.rotateXEnabled = false;
        this.rotateYEnabled = false;
        this.rotateZEnabled = false;
        this.moveXZEnabled = false;
        this.moveYEnabled = false;
        this.showElementInteractors = false;
        this.showMainElementSelection = false;
        this.app.coreApp.tools.stopActiveTool();
    }
}